import React, { useState, useEffect, useRef } from 'react'
import * as FiIcons from 'react-icons/fi'
import SafeIcon from '../../SafeIcon'
import supabase, { lsGet, lsSet, fetchParallel } from '../../../supabase/supabase'

const {
  FiTrendingUp, FiShoppingBag, FiDollarSign,
  FiBox, FiRefreshCw, FiChevronDown, FiBarChart2
} = FiIcons

const CACHE_KEY_PREFIX = 'dash_overview_'

// ─── Stat Card ────────────────────────────────────────────────────────────────
const StatCard = ({ title, value, sub, icon, color, loading, isDark, highlight }) => (
  <div className={`rounded-2xl border p-6 shadow-sm transition-all ${
    highlight
      ? isDark ? 'bg-gradient-to-br from-emerald-900/60 to-emerald-800/30 border-emerald-700/50' : 'bg-gradient-to-br from-emerald-50 to-green-50 border-emerald-200'
      : isDark ? 'bg-[#1e293b] border-[#334155]' : 'bg-white border-gray-100'
  }`}>
    <div className="flex justify-between items-start mb-4">
      <div className={`w-11 h-11 rounded-xl flex items-center justify-center ${color}`}>
        <SafeIcon icon={icon} className="w-5 h-5 text-white" />
      </div>
    </div>
    {loading ? (
      <div className="animate-pulse">
        <div className={`h-8 w-24 rounded mb-2 ${isDark ? 'bg-[#334155]' : 'bg-gray-100'}`} />
        <div className={`h-4 w-32 rounded ${isDark ? 'bg-[#1e293b]' : 'bg-gray-50'}`} />
      </div>
    ) : (
      <>
        <div className={`text-2xl font-bold ${highlight ? (isDark ? 'text-emerald-300' : 'text-emerald-700') : (isDark ? 'text-white' : 'text-gray-900')}`}>{value}</div>
        <div className={`text-sm mt-0.5 font-medium ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>{title}</div>
        {sub && <div className={`text-xs mt-1 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>{sub}</div>}
      </>
    )}
  </div>
)

const SalesChart = ({ data, isDark }) => {
  const [hover, setHover] = useState(null)
  if (!data.length) return (
    <div className={`flex items-center justify-center h-48 text-sm ${isDark ? 'text-gray-500' : 'text-gray-300'}`}>No sales data yet</div>
  )
  const maxRev = Math.max(...data.map(d => d.revenue), 1)
  return (
    <div className="space-y-2">
      <div className="flex items-center space-x-4 text-xs mb-3">
        <div className="flex items-center space-x-1.5"><div className="w-3 h-3 rounded-sm bg-green-500" /><span className={isDark ? 'text-gray-400' : 'text-gray-500'}>Revenue</span></div>
        <div className="flex items-center space-x-1.5"><div className="w-3 h-3 rounded-sm bg-blue-400" /><span className={isDark ? 'text-gray-400' : 'text-gray-500'}>Profit</span></div>
      </div>
      <div className="flex items-end space-x-1.5 h-44">
        {data.map((d, i) => (
          <div key={i} className="flex-1 flex flex-col items-center space-y-1 relative"
            onMouseEnter={() => setHover(i)} onMouseLeave={() => setHover(null)}>
            {hover === i && (
              <div className={`absolute -top-14 left-1/2 -translate-x-1/2 z-10 px-2.5 py-1.5 rounded-lg text-[10px] font-bold whitespace-nowrap shadow-xl border ${isDark ? 'bg-[#1e293b] border-[#334155] text-white' : 'bg-white border-gray-100 text-gray-800'}`}>
                <div className="text-green-500">Rev: R{d.revenue.toFixed(0)}</div>
                <div className="text-blue-400">Profit: R{d.profit.toFixed(0)}</div>
              </div>
            )}
            <div className="w-full flex items-end space-x-0.5" style={{ height: '120px' }}>
              <div className={`flex-1 rounded-t-md transition-all duration-700 ${isDark ? 'bg-gradient-to-t from-green-600 to-green-400' : 'bg-gradient-to-t from-green-500 to-green-400'}`}
                style={{ height: `${Math.max((d.revenue / maxRev) * 100, d.revenue > 0 ? 3 : 0)}%` }} />
              <div className={`flex-1 rounded-t-md transition-all duration-700 ${isDark ? 'bg-gradient-to-t from-blue-600 to-blue-400' : 'bg-gradient-to-t from-blue-500 to-blue-400'}`}
                style={{ height: `${Math.max((d.profit / maxRev) * 100, d.profit > 0 ? 3 : 0)}%` }} />
            </div>
            <div className={`text-[9px] font-medium truncate w-full text-center ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>{d.label}</div>
          </div>
        ))}
      </div>
    </div>
  )
}

const RecentOrderRow = ({ order, isDark }) => {
  const statusColors = {
    completed: isDark ? 'bg-green-900/30 text-green-400' : 'bg-green-100 text-green-700',
    pending: isDark ? 'bg-yellow-900/30 text-yellow-400' : 'bg-yellow-100 text-yellow-700',
    cancelled: isDark ? 'bg-red-900/30 text-red-400' : 'bg-red-100 text-red-600',
    refunded: isDark ? 'bg-gray-700 text-gray-300' : 'bg-gray-100 text-gray-500',
  }
  const paidOk = order.payment_status === 'successful'
  return (
    <tr className={`border-b transition-colors ${isDark ? 'border-[#334155] hover:bg-[#334155]/30' : 'border-gray-50 hover:bg-gray-50/50'}`}>
      <td className="py-3 px-4">
        <div className={`font-medium text-sm ${isDark ? 'text-gray-200' : 'text-gray-800'}`}>{order.customer_name}</div>
        <div className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>{order.customer_email}</div>
      </td>
      <td className={`py-3 px-4 text-sm font-bold ${isDark ? 'text-white' : 'text-gray-800'}`}>R{Number(order.total).toFixed(2)}</td>
      <td className="py-3 px-4">
        <span className={`px-2.5 py-1 rounded-full text-[10px] font-black uppercase tracking-wider ${paidOk ? (isDark ? 'bg-green-900/30 text-green-400' : 'bg-green-100 text-green-700') : (isDark ? 'bg-yellow-900/30 text-yellow-400' : 'bg-yellow-100 text-yellow-700')}`}>
          {paidOk ? 'Paid' : order.payment_status || 'pending'}
        </span>
      </td>
      <td className="py-3 px-4">
        <span className={`px-2.5 py-1 rounded-full text-[10px] font-black uppercase tracking-wider ${statusColors[order.status] || (isDark ? 'bg-gray-700 text-gray-300' : 'bg-gray-100 text-gray-500')}`}>
          {order.status}
        </span>
      </td>
      <td className={`py-3 px-4 text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
        {new Date(order.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
      </td>
    </tr>
  )
}

const ProfitBreakdown = ({ stats, isDark }) => {
  const margin = stats.revenue > 0 ? ((stats.profit / stats.revenue) * 100).toFixed(1) : '0.0'
  const rows = [
    { label: 'Gross Revenue', value: `R${stats.revenue.toFixed(2)}`, color: isDark ? 'text-green-400' : 'text-green-600' },
    { label: 'Est. Cost of Goods', value: `- R${stats.cogs.toFixed(2)}`, color: isDark ? 'text-red-400' : 'text-red-500' },
    { label: 'Gross Profit', value: `R${stats.profit.toFixed(2)}`, color: isDark ? 'text-blue-400' : 'text-blue-600', bold: true },
    { label: 'Profit Margin', value: `${margin}%`, color: isDark ? 'text-purple-400' : 'text-purple-600' },
  ]
  return (
    <div className={`rounded-2xl border shadow-sm p-6 ${isDark ? 'bg-[#1e293b] border-[#334155]' : 'bg-white border-gray-100'}`}>
      <div className="flex items-center space-x-2 mb-5">
        <div className="w-8 h-8 rounded-lg bg-blue-500 flex items-center justify-center">
          <SafeIcon icon={FiBarChart2} className="w-4 h-4 text-white" />
        </div>
        <div>
          <h3 className={`font-semibold text-sm ${isDark ? 'text-white' : 'text-gray-800'}`}>Profit Breakdown</h3>
          <p className={`text-[10px] ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>Paid orders only · based on product cost prices</p>
        </div>
      </div>
      <div className="space-y-3">
        {rows.map((r, i) => (
          <div key={i}>
            {i === rows.length - 2 && <div className={`border-t my-3 ${isDark ? 'border-[#334155]' : 'border-gray-100'}`} />}
            <div className="flex items-center justify-between">
              <span className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>{r.label}</span>
              <span className={`text-sm ${r.bold ? 'font-bold' : 'font-medium'} ${r.color}`}>{r.value}</span>
            </div>
          </div>
        ))}
      </div>
      {stats.productsWithCost === 0 && (
        <div className={`mt-4 p-3 rounded-xl text-xs ${isDark ? 'bg-amber-900/20 text-amber-400 border border-amber-800/30' : 'bg-amber-50 text-amber-700 border border-amber-100'}`}>
          ⚠️ No products have a cost price set. Add cost prices to products to see accurate profit data.
        </div>
      )}
    </div>
  )
}

export default function DashboardOverview({ projectId, isDark = false }) {
  const [stats, setStats] = useState({ revenue: 0, profit: 0, cogs: 0, orders: 0, paidOrders: 0, products: 0, avgOrder: 0, productsWithCost: 0 })
  const [recentOrders, setRecentOrders] = useState([])
  const [chartData, setChartData] = useState([])
  const [loading, setLoading] = useState(true)
  const [range, setRange] = useState('7')
  const isMounted = useRef(true)

  useEffect(() => { isMounted.current = true; return () => { isMounted.current = false } }, [])

  useEffect(() => { if (projectId) fetchAll() }, [range, projectId])

  const fetchAll = async (force = false) => {
    const cacheKey = `${CACHE_KEY_PREFIX}${projectId}_${range}`

    // Show cached data immediately (non-blocking)
    if (!force) {
      const cached = lsGet(cacheKey)
      if (cached) {
        if (isMounted.current) {
          setStats(cached.stats)
          setRecentOrders(cached.recentOrders)
          setChartData(cached.chartData)
          setLoading(false)
        }
        // Still revalidate in background
        _doFetch(cacheKey)
        return
      }
    }

    setLoading(true)
    await _doFetch(cacheKey)
  }

  const _doFetch = async (cacheKey) => {
    try {
      const days = parseInt(range)
      const since = new Date()
      since.setDate(since.getDate() - days)
      const sinceISO = since.toISOString()

      // Parallel fetch
      const results = await fetchParallel([
        () => supabase.from('store_orders_1772955957776').select('*').eq('project_id', projectId).gte('created_at', sinceISO).order('created_at', { ascending: false }),
        () => supabase.from('store_products_1772955957776').select('id,price,cost_price').eq('project_id', projectId),
      ])

      if (!isMounted.current) return

      const orders = results[0].status === 'fulfilled' ? (results[0].value.value?.data || []) : []
      const products = results[1].status === 'fulfilled' ? (results[1].value.value?.data || []) : []

      const paidOrders = orders.filter(o => o.payment_status === 'successful')
      const totalRevenue = paidOrders.reduce((s, o) => s + Number(o.total), 0)
      const avgOrder = paidOrders.length ? totalRevenue / paidOrders.length : 0

      const productsWithCost = products.filter(p => Number(p.cost_price) > 0 && Number(p.price) > 0)
      let avgMarginRate = 0
      if (productsWithCost.length > 0) {
        const totalMargin = productsWithCost.reduce((s, p) => s + (Number(p.price) - Number(p.cost_price)) / Number(p.price), 0)
        avgMarginRate = totalMargin / productsWithCost.length
      }
      const estimatedProfit = totalRevenue * avgMarginRate
      const estimatedCOGS = totalRevenue - estimatedProfit

      const newStats = { revenue: totalRevenue, profit: estimatedProfit, cogs: estimatedCOGS, orders: orders.length, paidOrders: paidOrders.length, products: products.length, avgOrder, productsWithCost: productsWithCost.length }
      const newRecentOrders = orders.slice(0, 6)

      const buckets = []
      for (let i = Math.min(days, 7) - 1; i >= 0; i--) {
        const d = new Date(); d.setDate(d.getDate() - i)
        const label = d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
        const dayStr = d.toISOString().slice(0, 10)
        const dayRevenue = paidOrders.filter(o => o.created_at.slice(0, 10) === dayStr).reduce((s, o) => s + Number(o.total), 0)
        buckets.push({ label, revenue: dayRevenue, profit: dayRevenue * avgMarginRate })
      }

      if (isMounted.current) {
        setStats(newStats); setRecentOrders(newRecentOrders); setChartData(buckets)
      }

      // Cache the result
      lsSet(cacheKey, { stats: newStats, recentOrders: newRecentOrders, chartData: buckets })
    } catch (e) {
      console.error(e)
    } finally {
      if (isMounted.current) setLoading(false)
    }
  }

  const fmt = (n) => `R${Number(n).toFixed(2)}`
  const bg = isDark ? 'bg-[#0f172a]' : 'bg-[#fafbfc]'
  const cardBg = isDark ? 'bg-[#1e293b]' : 'bg-white'
  const border = isDark ? 'border-[#334155]' : 'border-gray-100'
  const titleColor = isDark ? 'text-white' : 'text-gray-800'
  const subTitleColor = isDark ? 'text-gray-400' : 'text-gray-400'
  const inputBg = isDark ? 'bg-[#1e293b]' : 'bg-white'
  const inputBorder = isDark ? 'border-[#334155]' : 'border-gray-200'
  const inputText = isDark ? 'text-gray-200' : 'text-gray-700'

  return (
    <div className={`absolute inset-0 overflow-y-auto p-8 custom-scrollbar ${bg}`}>
      <div className="max-w-6xl mx-auto space-y-6 pb-12">
        <div className="flex items-center justify-between">
          <div>
            <h1 className={`text-2xl font-bold ${titleColor}`}>Store Dashboard</h1>
            <p className={`text-sm mt-0.5 ${subTitleColor}`}>Revenue & profit from paid orders only</p>
          </div>
          <div className="flex items-center space-x-3">
            <button onClick={() => fetchAll(true)} className={`p-2 rounded-xl border transition-colors ${isDark ? 'bg-[#1e293b] border-[#334155] hover:bg-[#334155]/50' : 'bg-white border-gray-200 hover:bg-gray-50'}`}>
              <SafeIcon icon={FiRefreshCw} className={`w-4 h-4 ${loading ? 'animate-spin' : ''} ${isDark ? 'text-gray-400' : 'text-gray-500'}`} />
            </button>
            <div className="relative">
              <select value={range} onChange={e => setRange(e.target.value)}
                className={`appearance-none rounded-xl pl-4 pr-9 py-2.5 text-sm font-medium shadow-sm cursor-pointer outline-none border transition-colors ${inputBg} ${inputBorder} ${inputText}`}>
                <option value="7">Last 7 days</option>
                <option value="14">Last 14 days</option>
                <option value="30">Last 30 days</option>
              </select>
              <SafeIcon icon={FiChevronDown} className={`absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 pointer-events-none ${isDark ? 'text-gray-500' : 'text-gray-400'}`} />
            </div>
          </div>
        </div>

        <div className={`flex items-center space-x-2 px-4 py-2.5 rounded-xl border text-xs font-medium ${isDark ? 'bg-green-900/10 border-green-900/30 text-green-400' : 'bg-green-50 border-green-100 text-green-700'}`}>
          <div className="w-2 h-2 rounded-full bg-green-500 animate-pulse" />
          <span>Revenue & profit calculated from <strong>successfully paid orders only</strong> — processing/pending orders are excluded.</span>
        </div>

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <StatCard title="Total Revenue" value={fmt(stats.revenue)} sub={`${stats.paidOrders} paid order${stats.paidOrders !== 1 ? 's' : ''}`} icon={FiDollarSign} color="bg-emerald-500" loading={loading} isDark={isDark} highlight />
          <StatCard title="Gross Profit" value={fmt(stats.profit)} sub={stats.productsWithCost > 0 ? `${((stats.profit / Math.max(stats.revenue, 1)) * 100).toFixed(1)}% margin` : 'Set cost prices on products'} icon={FiBarChart2} color="bg-blue-500" loading={loading} isDark={isDark} />
          <StatCard title="Avg. Order Value" value={fmt(stats.avgOrder)} sub="paid orders" icon={FiTrendingUp} color="bg-purple-500" loading={loading} isDark={isDark} />
          <StatCard title="Active Products" value={stats.products} sub={`${stats.productsWithCost} with cost price`} icon={FiBox} color="bg-amber-500" loading={loading} isDark={isDark} />
        </div>

        <div className="grid grid-cols-5 gap-6">
          <div className={`col-span-3 rounded-2xl border shadow-sm p-6 ${cardBg} ${border}`}>
            <div className="flex items-center justify-between mb-4">
              <h3 className={`font-semibold ${titleColor}`}>Revenue vs Profit</h3>
              <span className={`text-xs ${subTitleColor}`}>Paid orders only</span>
            </div>
            <SalesChart data={chartData} isDark={isDark} />
          </div>
          <div className="col-span-2"><ProfitBreakdown stats={stats} isDark={isDark} /></div>
        </div>

        <div className="grid grid-cols-5 gap-6">
          <div className={`col-span-2 rounded-2xl border shadow-sm p-6 ${cardBg} ${border}`}>
            <h3 className={`font-semibold mb-4 ${titleColor}`}>Order Status</h3>
            {['completed', 'pending', 'cancelled', 'refunded'].map(status => {
              const count = recentOrders.filter(o => o.status === status).length
              const pct = recentOrders.length ? Math.round((count / recentOrders.length) * 100) : 0
              const colors = { completed: 'bg-emerald-500', pending: 'bg-yellow-400', cancelled: 'bg-red-400', refunded: isDark ? 'bg-gray-600' : 'bg-gray-300' }
              return (
                <div key={status} className="mb-3">
                  <div className={`flex justify-between text-xs font-medium mb-1 capitalize ${isDark ? 'text-gray-400' : 'text-gray-600'}`}><span>{status}</span><span>{count} ({pct}%)</span></div>
                  <div className={`h-2 rounded-full overflow-hidden ${isDark ? 'bg-[#334155]' : 'bg-gray-100'}`}>
                    <div className={`h-full rounded-full ${colors[status]} transition-all duration-700`} style={{ width: `${pct}%` }} />
                  </div>
                </div>
              )
            })}
            {!recentOrders.length && !loading && <p className={`text-sm text-center py-6 ${isDark ? 'text-gray-500' : 'text-gray-300'}`}>No orders yet</p>}
            <div className={`mt-5 pt-4 border-t ${isDark ? 'border-[#334155]' : 'border-gray-100'}`}>
              <p className={`text-[10px] font-black uppercase tracking-widest mb-3 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>Payment Status</p>
              {['successful', 'pending', 'failed'].map(ps => {
                const count = recentOrders.filter(o => (o.payment_status || 'pending') === ps).length
                const pct = recentOrders.length ? Math.round((count / recentOrders.length) * 100) : 0
                const colors = { successful: 'bg-green-500', pending: 'bg-yellow-400', failed: 'bg-red-400' }
                const labels = { successful: 'Paid', pending: 'Pending', failed: 'Failed' }
                return (
                  <div key={ps} className="mb-2">
                    <div className={`flex justify-between text-xs font-medium mb-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}><span>{labels[ps]}</span><span>{count}</span></div>
                    <div className={`h-1.5 rounded-full overflow-hidden ${isDark ? 'bg-[#334155]' : 'bg-gray-100'}`}>
                      <div className={`h-full rounded-full ${colors[ps]} transition-all duration-700`} style={{ width: `${pct}%` }} />
                    </div>
                  </div>
                )
              })}
            </div>
          </div>

          <div className={`col-span-3 rounded-2xl border shadow-sm overflow-hidden ${cardBg} ${border}`}>
            <div className={`px-6 py-4 border-b flex items-center justify-between ${isDark ? 'border-[#334155]' : 'border-gray-50'}`}>
              <h3 className={`font-semibold ${titleColor}`}>Recent Orders</h3>
              <span className={`text-xs ${subTitleColor}`}>{recentOrders.length} orders</span>
            </div>
            {loading && recentOrders.length === 0 ? (
              <div className="p-8 flex justify-center">
                <div className="w-8 h-8 border-4 border-green-500/30 border-t-green-500 rounded-full animate-spin" />
              </div>
            ) : recentOrders.length === 0 ? (
              <div className={`py-12 text-center text-sm ${isDark ? 'text-gray-500' : 'text-gray-300'}`}>No orders in this period</div>
            ) : (
              <table className="w-full">
                <thead className={isDark ? 'bg-[#0f172a]' : 'bg-gray-50/80'}>
                  <tr>{['Customer', 'Total', 'Paid', 'Status', 'Date'].map(h => (
                    <th key={h} className={`text-left text-[10px] font-black uppercase tracking-wider px-4 py-3 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>{h}</th>
                  ))}</tr>
                </thead>
                <tbody>{recentOrders.map(o => <RecentOrderRow key={o.id} order={o} isDark={isDark} />)}</tbody>
              </table>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}